/**
 * Live feed over the API's SSE endpoint (GET /v1/stream), read with fetch so reconnects, cursors and failures are
 * fully under our control:
 *  - history is bootstrapped from GET /v1/events, then the stream resumes from the last seen `seq` with both the
 *    `Last-Event-ID` header and `?after=`, so nothing is skipped or duplicated across reconnects;
 *  - `chain.reorg.v1` drops every held event above `data.ancestorBlock` (corrected events follow);
 *  - while disconnected nothing is synthesised: the list simply stops growing and the state says so.
 */
import type { FeedEventDto } from '@runner/schema';
import { RunnerApiClient } from './client';

export type FeedConnState = 'connecting' | 'live' | 'reconnecting' | 'disconnected';

export interface SseFrame {
  id?: string;
  event?: string;
  data: string;
}

/** Incremental SSE parser: returns complete frames and the unconsumed remainder. Comments (heartbeats) are dropped. */
export function parseSse(buffer: string): { frames: SseFrame[]; rest: string } {
  const frames: SseFrame[] = [];
  const norm = buffer.replace(/\r\n/g, '\n');
  const parts = norm.split('\n\n');
  const rest = parts.pop() ?? '';
  for (const block of parts) {
    let id: string | undefined;
    let event: string | undefined;
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (!line || line.startsWith(':')) continue;
      const i = line.indexOf(':');
      const field = i < 0 ? line : line.slice(0, i);
      const value = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '');
      if (field === 'id') id = value;
      else if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (data.length || event) frames.push({ id, event, data: data.join('\n') });
  }
  return { frames, rest };
}

export interface ApplyResult {
  events: FeedEventDto[];
  /** Events removed because of a reorg notice. */
  dropped: number;
  reorgAncestor: bigint | null;
  added: boolean;
}

/**
 * Pure feed reducer (unit-tested). Keeps events ordered by seq, ignores duplicates, and on `chain.reorg.v1` drops
 * every held event whose block is above the common ancestor before recording the notice itself.
 */
export function applyFeedEvent(events: FeedEventDto[], e: FeedEventDto, max = 300): ApplyResult {
  if (events.some((x) => x.seq === e.seq)) return { events, dropped: 0, reorgAncestor: null, added: false };
  let next = events;
  let dropped = 0;
  let reorgAncestor: bigint | null = null;
  if (e.type === 'chain.reorg.v1') {
    const raw = (e.data as { ancestorBlock?: unknown }).ancestorBlock;
    reorgAncestor = BigInt(String(raw ?? e.blockNumber));
    const kept = next.filter((x) => x.type === 'chain.reorg.v1' || BigInt(x.blockNumber) <= reorgAncestor!);
    dropped = next.length - kept.length;
    next = kept;
  }
  next = [...next, e].sort((a, b) => a.seq - b.seq);
  if (next.length > max) next = next.slice(next.length - max);
  return { events: next, dropped, reorgAncestor, added: true };
}

export interface FeedHandlers {
  onChange(): void;
  onEvent?(e: FeedEventDto): void;
  onReorg?(ancestor: bigint, dropped: number): void;
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      resolve();
    });
  });

export class FeedClient {
  events: FeedEventDto[] = [];
  lastSeq = 0;
  state: FeedConnState = 'connecting';
  failures = 0;
  reorgs = 0;
  lastFrameAt = 0;
  latestSeq = 0;
  private stopped = true;
  private ctl: AbortController | null = null;
  private bootstrapped = false;

  constructor(
    private readonly api: RunnerApiClient,
    private readonly h: FeedHandlers,
    private readonly opts: { history?: number; watchdogMs?: number } = {},
  ) {}

  start() {
    if (!this.stopped) return;
    this.stopped = false;
    void this.loop();
  }

  stop() {
    this.stopped = true;
    this.ctl?.abort();
  }

  private set(state: FeedConnState) {
    if (this.state !== state) {
      this.state = state;
      this.h.onChange();
    }
  }

  private ingest(e: FeedEventDto) {
    const r = applyFeedEvent(this.events, e);
    if (!r.added) return;
    this.events = r.events;
    this.lastSeq = Math.max(this.lastSeq, e.seq);
    this.latestSeq = Math.max(this.latestSeq, e.seq);
    if (r.reorgAncestor !== null) {
      this.reorgs++;
      this.h.onReorg?.(r.reorgAncestor, r.dropped);
    }
    this.h.onEvent?.(e);
  }

  private async bootstrap() {
    const head = await this.api.events(9_000_000_000_000, 1);
    this.latestSeq = head.latestSeq;
    const from = Math.max(0, head.latestSeq - (this.opts.history ?? 150));
    const page = await this.api.events(from, 200);
    for (const e of page.events) this.ingest(e);
    this.lastSeq = Math.max(this.lastSeq, page.nextCursor, from);
    this.bootstrapped = true;
    this.h.onChange();
  }

  private async loop() {
    while (!this.stopped) {
      try {
        if (!this.bootstrapped) await this.bootstrap();
        await this.connectOnce();
        // Server closed the stream normally (max duration): reconnect immediately with the cursor.
        this.failures = 0;
      } catch {
        if (this.stopped) break;
        this.failures++;
      }
      if (this.stopped) break;
      this.set(this.failures >= 2 ? 'disconnected' : 'reconnecting');
      const backoff = this.failures === 0 ? 250 : Math.min(10_000, 500 * 2 ** Math.min(this.failures, 5));
      await sleep(backoff);
    }
  }

  private async connectOnce() {
    const ctl = new AbortController();
    this.ctl = ctl;
    const watchdogMs = this.opts.watchdogMs ?? 45_000;
    let watchdog = setTimeout(() => ctl.abort(), watchdogMs);
    const kick = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => ctl.abort(), watchdogMs);
    };
    try {
      const res = await fetch(`${this.api.base}/v1/stream?after=${this.lastSeq}`, {
        headers: { Accept: 'text/event-stream', 'Last-Event-ID': String(this.lastSeq) },
        cache: 'no-store',
        signal: ctl.signal,
      });
      if (!res.ok || !res.body) throw new Error(`stream HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) return;
        kick();
        this.lastFrameAt = Date.now();
        buf += dec.decode(value, { stream: true });
        const { frames, rest } = parseSse(buf);
        buf = rest;
        for (const f of frames) {
          if (f.event === 'stream.hello.v1') {
            try {
              const hello = JSON.parse(f.data) as { latestSeq: number };
              this.latestSeq = Math.max(this.latestSeq, hello.latestSeq);
            } catch {
              /* malformed hello: keep going, events carry their own seq */
            }
            this.failures = 0;
            this.set('live');
            continue;
          }
          if (!f.id) continue; // only persisted events move the cursor
          try {
            this.ingest(JSON.parse(f.data) as FeedEventDto);
          } catch {
            /* ignore a malformed frame; the cursor does not move past it */
          }
        }
        this.h.onChange();
      }
    } finally {
      clearTimeout(watchdog);
      if (this.ctl === ctl) this.ctl = null;
    }
  }
}

export { sleep };
